import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Asset, useUpdateMutation } from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Menu, Modal, Portal } from 'react-native-paper';
import { CreateFileForm } from '../../files/components/createFileForm';

type MediaType = 'file' | 'link';

type Props = {
  asset: Asset;
  invalidateQuery: () => void;
  disabled?: boolean;
};

export const AddFilesLinksButton = ({
  asset,
  invalidateQuery,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<MediaType | null>(null);

  const mediaIdsRef = useRef<Set<string>>(
    new Set((asset?.media || []).map((m) => m.id))
  );

  useEffect(() => {
    for (const m of asset?.media || []) {
      mediaIdsRef.current.add(m.id);
    }
  }, [asset?.media]);

  const openMenu = () => setVisible(true);
  const closeMenu = () => setVisible(false);

  const handleTypeSelect = (type: MediaType) => {
    setSelectedType(type);
    closeMenu();
  };

  const handleModalComplete = () => {
    setSelectedType(null);
  };

  const onAssetUploadComplete = async (id: string) => {
    mediaIdsRef.current.add(id);
    const media = Array.from(mediaIdsRef.current).map((mid) => ({ id: mid }));
    await updateAsset({ input: { id: asset.id, media } });
    await invalidateQuery();
    handleModalComplete();
  };

  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });

  return (
    <>
      <Portal>
        <Modal
          visible={!!selectedType}
          onDismiss={handleModalComplete}
          contentContainerStyle={styles.modalContainer}
        >
          {selectedType && (
            <CreateFileForm
              type={selectedType}
              isProductContext
              onProductUploadComplete={onAssetUploadComplete}
              onComplete={handleModalComplete}
            />
          )}
        </Modal>
      </Portal>

      <View style={styles.container}>
        <Menu
          visible={visible}
          onDismiss={closeMenu}
          contentStyle={styles.menuContent}
          anchor={
            <Pressable
              onPress={openMenu}
              style={styles.button}
              disabled={disabled}
            >
              <View style={styles.customButton}>
                <MaterialCommunityIcons
                  name="plus"
                  color={Colors.primaryDark}
                  size={16}
                  style={styles.icon}
                />
                <Text style={styles.buttonLabel}>
                  {t('asset.files.addFilesLinks')}
                </Text>
                <MaterialCommunityIcons
                  name={visible ? 'chevron-up' : 'chevron-down'}
                  color={Colors.primaryDark}
                  size={16}
                  style={styles.chevron}
                />
              </View>
            </Pressable>
          }
        >
          <Menu.Item
            leadingIcon="file-upload"
            onPress={() => handleTypeSelect('file')}
            title="File"
            style={styles.menuItem}
          />
          <Menu.Item
            leadingIcon="link"
            onPress={() => handleTypeSelect('link')}
            title="Link"
            style={styles.menuItem}
          />
        </Menu>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 30,
  },
  button: {
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderColor: Colors.primaryDark,
    borderWidth: 2,
  },
  buttonLabel: {
    color: Colors.primaryDark,
    fontWeight: '500',
    fontSize: 16,
  },
  icon: {
    marginRight: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: -0.25,
  },
  modalContainer: {
    flex: 1,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: 560,
    maxHeight: 600,
    borderRadius: 12,
    backgroundColor: Colors.primaryGrayBackground,
  },
  customButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContent: {
    backgroundColor: Colors.white,
    flexDirection: 'column',
    flex: 1,
    top: 30,
  },
  chevron: {
    marginLeft: 4,
  },
  menuItem: {
    backgroundColor: Colors.white,
  },
});

const UPDATE_ASSET = `
  mutation UpdateAsset($input: MutateAssetInput!) {
    updateAsset(input: $input) {
      id
    }
  }
`;
